import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SeoHead from './SeoHead';
import { Alert, Breadcrumbs, Button } from './ComponentLibrary';
import { pageMeta, SITE_URL } from '../seoUtils';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';
import { KB_CATEGORIES, kbCategoryLabel, POLICY_LINKS, policyLinkLabel } from '../config/kbCategories';
import './HelpCentre.css';

const SOURCE_LABEL = {
  OFFICIAL: 'Official',
  PLATFORM: 'Official',
  POLICY: 'Policy',
  GUIDE: 'Property Guide',
  CONTRIBUTOR: 'Community',
};

/**
 * A single-question FAQPage schema for FAQ-type KB articles - the title is
 * the question, the sanitized body (stripped to plain text) is the answer.
 * Only content_type === 'FAQ' articles get this; a how-to Help Article
 * isn't really a question-and-answer pair, so it stays as a plain page.
 */
function faqJsonLd(article) {
  if (article.content_type !== 'FAQ') return null;
  const answerText = String(article.body || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!answerText) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url: `${SITE_URL}/help/${article.slug}`,
    mainEntity: [
      {
        '@type': 'Question',
        name: article.title,
        acceptedAnswer: { '@type': 'Answer', text: answerText },
      },
    ],
  };
}

/* ─── Help Centre index — /help, optionally filtered by ?category=&q= ─── */

export function HelpCentreIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || '';
  const query = searchParams.get('q') || '';
  const [queryInput, setQueryInput] = useState(query);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (query) params.set('search', query);
    fetch(`${API_BASE}/kb/articles?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : { articles: [] }))
      .then((data) => {
        if (!cancelled) setArticles(Array.isArray(data.articles) ? data.articles : []);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCategory, query]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (queryInput.trim()) next.set('q', queryInput.trim());
    else next.delete('q');
    setSearchParams(next);
  };

  const categoryLabel = activeCategory ? kbCategoryLabel(activeCategory) : '';
  const seo = pageMeta({
    title: activeCategory ? `${categoryLabel} - Help Centre` : 'Help Centre',
    description: activeCategory
      ? KB_CATEGORIES.find((c) => c.slug === activeCategory)?.description
      : 'Search the NaijaNest Help Centre or browse help by topic: finding properties, listing, agents, messaging, reviews, safety, account and support.',
    path: activeCategory ? `/help?category=${activeCategory}` : '/help',
  });

  const breadcrumbItems = activeCategory
    ? [
        { label: 'Home', href: '/' },
        { label: 'Help Centre', href: '/help' },
        { label: categoryLabel, current: true },
      ]
    : [{ label: 'Home', href: '/' }, { label: 'Help Centre', current: true }];

  return (
    <div className="static-page help-centre">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs items={breadcrumbItems} />

      <section className="section help-centre__hero">
        <h1>How can we help?</h1>
        <p className="static-page__lead">
          Search the NaijaNest Help Centre, or browse by topic below.
        </p>
        <form className="help-centre__search" onSubmit={handleSearchSubmit} role="search">
          <input
            type="search"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder='Search NaijaNest Help — e.g. "how do I list a property"'
            aria-label="Search NaijaNest Help"
          />
          <button type="submit">Search</button>
        </form>
      </section>

      {!activeCategory && (
        <section className="section">
          <p className="help-centre__eyebrow">Browse by topic</p>
          <div className="help-centre__cat-grid">
            {KB_CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/help?category=${c.slug}`} className="help-centre__cat-card">
                <span className="help-centre__cat-icon" aria-hidden="true">
                  {c.icon}
                </span>
                <h3>{c.label}</h3>
                <p>{c.description}</p>
              </Link>
            ))}
          </div>

          <p className="help-centre__eyebrow">More from NaijaNest</p>
          <div className="help-centre__linkout-row">
            <Link to="/blog" className="help-centre__linkout-card">
              <span>
                <span className="help-centre__linkout-title">Property Guide</span>
                <span className="help-centre__linkout-sub">Renting, buying &amp; market guides</span>
              </span>
              <span aria-hidden="true">→</span>
            </Link>
            <div className="help-centre__linkout-card help-centre__linkout-card--policies">
              <span className="help-centre__linkout-title">Policies</span>
              <ul>
                {POLICY_LINKS.map((p) => (
                  <li key={p.path}>
                    <Link to={p.path}>{p.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <p className="help-centre__eyebrow">
          {activeCategory ? `Articles in ${categoryLabel}` : query ? `Results for "${query}"` : 'Latest articles'}
        </p>
        {activeCategory && (
          <p>
            <Link to="/help">← All topics</Link>
          </p>
        )}
        {loading ? (
          <Alert type="info">Loading…</Alert>
        ) : articles.length === 0 ? (
          <Alert type="info">No help articles found{query ? ` for "${query}"` : ''} yet.</Alert>
        ) : (
          <ul className="help-centre__article-list">
            {articles.map((a) => (
              <li key={a.id}>
                <Link to={`/help/${a.slug}`}>
                  <span className="help-centre__article-title">{a.title}</span>
                  <span className="help-centre__article-tag">{kbCategoryLabel(a.category)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ─── Article page — /help/:slug ─── */

export function HelpArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedTitles, setRelatedTitles] = useState({});
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setNotFound(false);
    setFeedbackGiven(false);
    fetch(`${API_BASE}/kb/articles/${encodeURIComponent(slug)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled) setArticle(data.article);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!article?.relatedArticleIds?.length) return;
    fetch(`${API_BASE}/kb/articles`)
      .then((res) => (res.ok ? res.json() : { articles: [] }))
      .then((data) => {
        const map = {};
        (data.articles || []).forEach((a) => {
          map[a.slug] = a.title;
        });
        setRelatedTitles(map);
      })
      .catch(() => {});
  }, [article]);

  // Mark links in the article body that point off-site, so a reader isn't
  // surprised by leaving NaijaNest - internal links (relative, or absolute
  // links back to this origin) are left alone. sanitizeArticleBody already
  // forces target=_blank/rel=noopener on every link server-side; this just
  // adds a visible "external" cue on top of that.
  useEffect(() => {
    if (!article || !bodyRef.current) return;
    const anchors = bodyRef.current.querySelectorAll('a[href]');
    anchors.forEach((a) => {
      const href = a.getAttribute('href') || '';
      const isExternal = /^https?:\/\//i.test(href) && !href.startsWith(window.location.origin);
      if (isExternal && !a.classList.contains('help-centre__external-link')) {
        a.classList.add('help-centre__external-link');
        a.insertAdjacentHTML('beforeend', ' <span aria-hidden="true">↗</span>');
      }
    });
  }, [article]);

  const seo = useMemo(() => {
    if (!article) return pageMeta({ title: 'Help Centre', path: `/help/${slug}`, noIndex: true });
    return pageMeta({
      title: article.seo_title || article.title,
      description: article.seo_description,
      path: `/help/${article.slug}`,
    });
  }, [article, slug]);

  const submitFeedback = (helpful) => {
    if (feedbackGiven || !article) return;
    setFeedbackGiven(true);
    fetch(`${API_BASE}/kb/articles/${article.id}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ helpful }),
    }).catch(() => {});
  };

  if (notFound) {
    return (
      <div className="static-page help-centre">
        <SeoHead title="Article not found | NaijaNest Help" description="" url="" noIndex />
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Help Centre', href: '/help' }, { label: 'Not found', current: true }]} />
        <section className="section">
          <Alert type="error">
            We couldn&apos;t find that Help Centre article. <Link to="/help">Back to Help Centre</Link>
          </Alert>
        </section>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="static-page help-centre">
        <section className="section">
          <Alert type="info">Loading…</Alert>
        </section>
      </div>
    );
  }

  return (
    <div className="static-page help-centre">
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        jsonLd={faqJsonLd(article)}
      />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Help Centre', href: '/help' },
          { label: kbCategoryLabel(article.category), href: `/help?category=${article.category}` },
          { label: article.title, current: true },
        ]}
      />

      {user?.role === 'admin' && article.status !== 'published' && (
        <section className="section">
          <Alert type="warning">
            Admin preview: this article is currently <strong>{article.status}</strong> and not visible to customers.
          </Alert>
        </section>
      )}

      <section className="section help-centre__article">
        <div className="help-centre__article-main">
          <h1>{article.title}</h1>
          <div className="help-centre__article-meta">
            <span className="help-centre__source-chip">{SOURCE_LABEL[article.source_type] || 'Official'}</span>
            <span>Updated {new Date(article.updated_at).toLocaleDateString()}</span>
          </div>
          <div
            ref={bodyRef}
            className="help-centre__article-body"
            dangerouslySetInnerHTML={{ __html: article.body }}
          />

          {article.ctaLinks?.some((l) => l.type === 'action') && (
            <div className="help-centre__cta-row">
              {article.ctaLinks
                .filter((l) => l.type === 'action')
                .map((l) => (
                  <Button key={`${l.url}-${l.label}`} onClick={() => navigate(l.url)}>
                    {l.label}
                  </Button>
                ))}
            </div>
          )}

          <div className="help-centre__feedback">
            {feedbackGiven ? (
              <span>Thanks for the feedback.</span>
            ) : (
              <>
                <span>Was this article helpful?</span>
                <div className="help-centre__feedback-btns">
                  <button type="button" onClick={() => submitFeedback(true)}>
                    👍 Yes
                  </button>
                  <button type="button" onClick={() => submitFeedback(false)}>
                    👎 No
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {(article.relatedArticleIds?.length > 0 ||
          article.relatedPolicyPaths?.length > 0 ||
          article.ctaLinks?.some((l) => l.type !== 'action')) && (
          <aside className="help-centre__sidebar">
            {article.ctaLinks?.some((l) => l.type !== 'action') && (
              <div className="help-centre__side-card">
                <h4>Explore</h4>
                <ul>
                  {article.ctaLinks
                    .filter((l) => l.type !== 'action')
                    .map((l) => (
                      <li key={`${l.type}-${l.url}`}>
                        <Link to={l.url}>{l.label}</Link>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {article.relatedArticleIds?.length > 0 && (
              <div className="help-centre__side-card">
                <h4>Related articles</h4>
                <ul>
                  {article.relatedArticleIds.map((relSlug) => (
                    <li key={relSlug}>
                      <Link to={`/help/${relSlug}`}>{relatedTitles[relSlug] || relSlug}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {article.relatedPolicyPaths?.length > 0 && (
              <div className="help-centre__side-card help-centre__side-card--policy">
                <h4>Related policies</h4>
                <ul>
                  {article.relatedPolicyPaths.map((path) => (
                    <li key={path}>
                      <Link to={path}>{policyLinkLabel(path)}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        )}
      </section>
    </div>
  );
}
